<template>
  <img src="@/assets/images/evaluation/background.svg" class="bg" />
  <div v-if="isFinished" class="expired_container">
    <img class="expired_icon" src="@/assets/images/evaluation/evaluation_icon.svg" />
    <div class="expired_txt">Thank you for your evaluation.</div>
    <div class="close_btn" @click="handleClose">Complete and close</div>
  </div>
  <div v-else-if="isExpired" class="expired_container">
    <img class="expired_icon" src="@/assets/images/evaluation/timer.svg" />
    <div class="expired_txt">This invitation has expired.</div>
    <div class="close_btn" @click="handleClose">Complete and close</div>
  </div>
  <div v-else class="container">
    <div class="img_wrapper">
      <img class="icon" src="@/assets/images/evaluation/evaluation_icon.svg" />
      <img class="logo" src="@/assets/images/evaluation/logo.svg" />
    </div>
    <div class="title">
      <span class="name">{{ userName }}</span>
      invites you to provide feedback on the resume
    </div>
    <div class="tips">After completion, your evaluation will appear on their resume.</div>
    <a-form ref="formRef" class="evaluation_form" :model="formState" layout="vertical">
      <a-form-item
        validateTrigger="blur"
        label="Your Name"
        name="evaluatorName"
        :rules="[{ required: true, message: 'Enter Your Name' }]"
      >
        <a-input v-model:value="formState.evaluatorName" placeholder="Your Name" :maxlength="20" />
      </a-form-item>
      <a-form-item
        validateTrigger="blur"
        label="Your Relationship"
        name="evaluatorRelation"
        :rules="[{ required: true, message: 'Enter Your Relationship' }]"
      >
        <a-input v-model:value="formState.evaluatorRelation" placeholder="Your Relationship" :maxlength="20" />
      </a-form-item>
      <a-form-item
        validateTrigger="blur"
        label="Your Feedback"
        name="textContent"
        :rules="[{ required: true, message: 'Enter Your Feedback' }]"
      >
        <a-textarea
          name="search"
          placeholder="Enter your review"
          :auto-size="{ minRows: 10 }"
          :maxlength="1200"
          :showCount="true"
          class="evaluation_textarea"
          v-model:value="formState.textContent"
        />
      </a-form-item>
      <div class="upload_label">Add a photo/video for review</div>
      <Upload @on-upload="handleOnUploaded" @on-upload-with-cover="handleOnUploadedWithCover" />
      <a-button
        type="primary"
        html-type="button"
        class="submit_btn"
        @click.prevent="handleSubmit"
        :disabled="isSubmitDisabled"
      >
        Confirm
        <CheckOutlined class="btn_icon" />
      </a-button>
    </a-form>
  </div>
</template>

<script setup>
import { CheckOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { onMounted, reactive, ref } from 'vue';

import { submitEvaluationWithToken, validEvaluationWithToken } from '@/api/resume.js';
import Upload from '@/components/Upload.vue';

const isExpired = ref(false);
const isFinished = ref(false);
const userName = ref('');
const formState = reactive({
  token: '',
  evaluatorName: '',
  evaluatorRelation: '',
  textContent: '',
  imageUrls: '',
  videoUrl: '',
  videoCoverUrl: '',
});

const formRef = ref(null);
const isSubmitDisabled = ref(false);
const handleOnUploaded = (fileUrl, fileType) => {
  formState.imageUrls = '';
  formState.videoUrl = '';
  formState.videoCoverUrl = '';
  if (fileType.startsWith('image/')) {
    formState.imageUrls = fileUrl;
  } else if (fileType.startsWith('video/')) {
    formState.videoUrl = fileUrl;
  }
};
const handleOnUploadedWithCover = (fileUrl, fileType, coverUrl) => {
  formState.imageUrls = '';
  formState.videoUrl = fileUrl;
  formState.videoCoverUrl = coverUrl;
};
const handleSubmit = () => {
  formRef.value.validate().then(() => {
    isSubmitDisabled.value = true;
    submitEvaluationWithToken(formState).then((res) => {
      if (res.code == 0) {
        message.success(res.msg);
        formState.evaluatorName = '';
        formState.evaluatorRelation = '';
        formState.textContent = '';
        formState.videoUrl = '';
        isFinished.value = true;
      } else {
        isExpired.value = true;
        message.error(res.msg);
      }
      isSubmitDisabled.value = false;
    });
  });
};
const handleClose = () => {
  window.location.href = '/index';
};
const handleValidEvaluationWithToken = () => {
  validEvaluationWithToken(formState.token).then((res) => {
    if (res.data == true) {
      isExpired.value = false;
    } else {
      isExpired.value = true;
    }
  });
};

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  formState.token = urlParams.get('token') || '';
  userName.value = urlParams.get('name') || '';
  handleValidEvaluationWithToken();
});
</script>

<style lang="less">
.evaluation_form {
  width: 100%;
  margin-top: 30px;
  background-color: #fff;
  border-radius: 20px;
  padding: 30px 50px;
  .ant-form-item {
    label {
      font-size: 14px;
      color: #3d3d3d;
      margin-bottom: 5px;
      font-weight: 500;
      &::before {
        display: none !important;
      }
    }
    .ant-form-item-control-input-content {
      .ant-input,
      .ant-textarea {
        border-radius: 4px;
        padding: 12px 24px;
        border: 1px solid rgba(5, 100, 79, 0.5);
        color: #05644f;
        border-radius: 16px;
        font-size: 14px;
        &.ant-input-status-error {
          border-color: #ff4d4f;
        }
        &::placeholder {
          color: rgba(5, 100, 79, 0.3);
        }
      }
    }
  }
  .upload_label {
    font-size: 14px;
    font-weight: 500;
    color: #3d3d3d;
    margin-bottom: 15px;
  }
  .submit_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;
    display: block;
    width: 80%;
    padding: 10px 20px;
    height: auto;
    margin: 50px auto 20px;
    &:hover {
      background: #05644f;
      color: #fff;
    }
    .btn_arrow {
      margin-left: 10px;
      font-size: 18px;
    }
  }
  .evaluation_textarea {
    .ant-input {
      padding-bottom: 50px !important;
    }
    &::after {
      position: absolute;
      bottom: 15px;
      right: 24px;
    }
  }
}
</style>

<style lang="less" scoped>
.bg {
  width: 100%;
  position: absolute;
  top: 0;
  z-index: -1;
}
.container {
  width: 800px;
  padding: 50px;
  margin: 0 auto;
  color: #05644f;
  .img_wrapper {
    position: relative;
    .icon {
      width: 80px;
      height: 80px;
    }
    .logo {
      width: 180px;
      height: auto;
      position: absolute;
      right: 0;
    }
  }
  .title {
    font-size: 20px;
    font-weight: bold;
    margin: 20px 0;
    .name {
      font-size: 34px;
    }
  }
  .tips {
    font-size: 16px;
    font-weight: 300;
    margin-bottom: 50px;
  }
}
.expired_container {
  width: 400px;
  padding: 200px 0;
  margin: 0 auto;
  color: #05644f;
  text-align: center;
  .expired_icon {
    width: 80px;
    height: 80px;
  }
  .expired_txt {
    font-size: 18px;
    font-weight: 500;
    margin: 20px 0;
  }
  .close_btn {
    font-size: 16px;
    font-weight: bold;
    color: #fff;
    background: #05644f;
    border-radius: 100px;
    display: inline-block;
    padding: 12px 32px;
    height: auto;
    margin-top: 30px;
    cursor: pointer;
    &:hover {
      background: #05644f;
      color: #fff;
    }
  }
}
</style>
